import { slugToTitle } from "@/lib/slug";
import { Button } from "@/shadcn/components/ui/button";

/**
 * SEO-safe fallback shell for the venue details route.
 * Used when the loader returns null (transient backend failure)
 * and by VenueDetailsErrorBoundary.
 * @param slug - route slug; the <h1> falls back to it when the venue name is unavailable
 * @param summary - venue_summary, when the loader resolved it
 * @param showReload - render a reload action
 */
export function VenueDetailsFallback({
  slug,
  summary,
  showReload = false,
}: {
  slug: string;
  summary?: string | null;
  showReload?: boolean;
}) {
  const title = slugToTitle(slug);
  return (
    // The testid is the SSR smoke test's discriminator: the shell's slug-derived <h1>
    // is otherwise indistinguishable from the real venue UI's.
    <main
      id="main"
      data-testid="venue-details-fallback"
      className="max-w-[1440px] mx-auto p-8 bg-bg-brand-secondary-default"
    >
      <h1 className="title-m-bold mb-4">{title}</h1>
      {/* No "loading" wording: Google reads placeholder copy as an empty page, and this
          shell is also the terminal state of the error boundary, where nothing is loading. */}
      <p className="body-m-regular text-fg-neutral-subtle">
        {summary?.trim() ||
          `Accessibility information for ${title} — entrance, step-free access, toilets and photos — on Sociability.`}
      </p>
      {showReload && (
        <Button
          variant="outline"
          className="mt-6"
          onClick={() => window.location.reload()}
        >
          Reload page
        </Button>
      )}
    </main>
  );
}
